import { useQuery } from '@tanstack/react-query';
import { SECTION_NAMES } from '@repo/common';
import type { ITopWinsData } from '../types/topWins.types';
import { fetchTopWins } from '../../../core/api/endpoints/topWins.api';
import { useAppStore, useViewMode, useSelectedSection, useBalance, useIsIdle } from '../../../store/app/appStore';
import { SINGLE_VIEW_MODE } from '../../../constants';

export const TOP_WINS_QUERY_KEY = ['TopWins'];

// BE rebuilds the top-wins snapshot on a 1-minute interval.
const POLL_INTERVAL_MS = 60_000;

// Returned while the query is disabled/loading or BE reports no qualifying wins.
const EMPTY_TOP_WINS: ITopWinsData = {
  featured: null,
  recent: [],
  refreshedAt: '',
  userCurrency: '',
};

export const useTopWins = (): ITopWinsData => {
  const apiHost = useAppStore((state) => state.settings.apiHost);
  const hasTopWinsSection = useAppStore((state) =>
    state.sections.some((section) => section.name === SECTION_NAMES.TOP_WIN_GAMES),
  );
  const viewMode = useViewMode();
  const selectedSection = useSelectedSection();
  const balance = useBalance();
  const isIdle = useIsIdle();
  const currency = (balance.currency || 'EUR').toUpperCase();

  // Multi-view polls so the rotating cards stay live; the single-view tab only
  // needs a one-shot fetch on mount (no rotation, no live ticking there).
  const isMulti = viewMode === 'multi';
  const isSingleView = viewMode === SINGLE_VIEW_MODE && selectedSection === SECTION_NAMES.TOP_WIN_GAMES;

  const { data } = useQuery<ITopWinsData, Error>({
    queryKey: [...TOP_WINS_QUERY_KEY, currency],
    queryFn: () => fetchTopWins(currency),
    // Only fetch where TopWins is actually shown, and only when BE configured it.
    enabled: !isIdle && Boolean(apiHost) && hasTopWinsSection && (isMulti || isSingleView),
    // Single view fetches once on mount; only multi-view keeps polling.
    refetchInterval: isMulti ? POLL_INTERVAL_MS : false,
    staleTime: POLL_INTERVAL_MS,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  return data ?? EMPTY_TOP_WINS;
};
